import { useReducer, useMemo, useEffect, useContext, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { EditorLabel } from 'components/Editors/EditorLabel';
import { IEditorProps } from 'components/Editors/Editor';
import { EditorValidationWrapper } from 'components/Editors/EditorValidationWrapper';
import './TimeSpanEditor.less';
import { TimeSpanEditorRow } from './TimeSpanEditorRow';
import { debounce } from 'debounce';
import {
  IPropertyChild,
  getChildValueAsString,
  getChildValue,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { submitTimeSpanValue } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { TimeSpanValue, timeIsEmpty, getIsoString, getValue, getTimeSpanDisplayValue } from 'utils/TimeSpanUtils';
import { AwaitableContext } from 'contexts/AwaitableContext';
import { useTrackFocus } from 'components/inputs/hooks/useTrackFocus';

interface ITimeSpanEditorProps extends IEditorProps {}

const getClassName = () => classNames('dpc-time-span-editor');

// hard coded max of 2 years, per legacy implementation.
const MAX_DAYS = 730;

const UPDATE_DEBOUNCE_INTERVAL = 300;

/**
 * Time Span Editor, with four range sliders for different time segments
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function TimeSpanEditor({ autoFocus, child, path, onChange }: ITimeSpanEditorProps) {
  const { editingContextId /* refreshContext */ } = useContext(PropertyEditorContext);
  const { addPromise, removePromise } = useContext(AwaitableContext);
  const pathString = path.join('-');
  const emitNullForEmpty = useRef(getChildValue(child) === null);
  const [time, dispatch] = useReducer(timeSpanReducer, getInitialState(child));
  const prevTimeDisplayString = useRef(getTimeSpanDisplayValue(time));
  const timeDisplayString = getTimeSpanDisplayValue(time);
  const { state, focusTrackingProps } = useTrackFocus();

  useEffect(() => {
    if (!state.current.isFocusWithin) {
      const newValue = getInitialState(child);
      dispatch({field: "days", value: newValue.days.toString()});
      dispatch({field: "hours", value: newValue.hours.toString()});
      dispatch({field: "minutes", value: newValue.minutes.toString()});
      dispatch({field: "seconds", value: newValue.seconds.toString()});
    }
  }, [child, state]);

  // we don't want to report 1000 times as they drag around, just when they stop,
  // so saveNull and saveTime are debounced:
  const saveNull = useMemo(
    () =>
      debounce(() => {
        onChange(null, path);
      }, UPDATE_DEBOUNCE_INTERVAL),
    [onChange, path]
  );
  const saveTime = useMemo(
    () =>
      debounce(async (t: TimeSpanEditorState) => {
        const timeSpanValue = getIsoString(t);
        // since we are calling the service directly, we need to make sure we ensure this submitted before EditComplete:
        const submitTimeSpanPromise = submitTimeSpanValue({ editingContextId, path, timeSpanValue });
        addPromise(pathString, submitTimeSpanPromise);
        submitTimeSpanPromise.finally(() => removePromise(pathString));

        // tell parent grid to update it's snapshot // TODO a full refresh? is this really / still necessary? I bet not.
        // refreshContext();
      }, UPDATE_DEBOUNCE_INTERVAL),
    [addPromise, editingContextId, path, pathString, removePromise]
  );

  const emitValue = useCallback((t: TimeSpanEditorState) => {
    saveNull.clear();
    saveTime.clear();
    const newTimeString = getTimeSpanDisplayValue(t);
    if (prevTimeDisplayString.current === newTimeString) {
      return; // don't emit value unless user has changed something
    }

    if (emitNullForEmpty.current && timeIsEmpty(t)) {
      saveNull();
    } else {
      saveTime(t);
    }

    prevTimeDisplayString.current = newTimeString;
  }, [saveNull, saveTime]);

  const setValue = useCallback((change: TimeSpanChangeAction) => {
    dispatch(change);
    // getting changed value immediately 
    const newValue = timeSpanReducer(time, change);
    emitValue(newValue);
  }, [emitValue, time]);

  return (
    <EditorLabel editor={child} path={path}>
      <EditorValidationWrapper editor={child}>
        <table {...focusTrackingProps} className={getClassName()}>
          <tbody>
            <TimeSpanEditorRow
              label="Seconds"
              id="seconds"
              min={0}
              max={59}
              value={time.seconds}
              onChange={setValue}
              autoFocus={autoFocus}
              pathString={pathString}
            />
            <TimeSpanEditorRow
              label="Minutes"
              id="minutes"
              min={0}
              max={59}
              value={time.minutes}
              onChange={setValue}
              pathString={pathString}
            />
            <TimeSpanEditorRow
              label="Hours"
              id="hours"
              min={0}
              max={23}
              value={time.hours}
              onChange={setValue}
              pathString={pathString}
            />
            <TimeSpanEditorRow
              label="Days"
              id="days"
              min={0}
              max={MAX_DAYS}
              value={time.days}
              onChange={setValue}
              pathString={pathString}
            />
            <tr>
              <td className="dpc-time-span-editor__selected-time" colSpan={3}>
                {timeDisplayString}
              </td>
            </tr>
          </tbody>
        </table>
      </EditorValidationWrapper>
    </EditorLabel>
  );
}

function getInitialState(editor: IPropertyChild) {
  const value = getChildValueAsString(editor); // ISO string or empty

  const duration = getValue(value);
  return {
    ...duration,
  };
}

function timeSpanReducer(prevState: TimeSpanEditorState, { field, value }: TimeSpanChangeAction) {
  const state = { ...prevState };
  const parsed = parseInt(value, 10);
  state[field] = isNaN(parsed) ? 0 : parsed;
  // cap values to limit free text
  state.seconds = Math.min(state.seconds, 59);
  state.minutes = Math.min(state.minutes, 59);
  state.hours = Math.min(state.hours, 23);
  state.days = Math.min(state.days, MAX_DAYS);
  return state;
}

export interface TimeSpanEditorState extends TimeSpanValue {}

export interface TimeSpanChangeAction {
  field: keyof TimeSpanEditorState;
  value: string;
}
